"use client"

import { StoreProduct, StoreProductVariant } from "@/types/store"
import { Heading } from "@medusajs/ui"
import Link from "next/link"
import Modal from "@modules/common/components/modal"
import Image from "next/image"
import { normalizeTextEncoding } from "@/lib/util/text-encoding"
import { getProductVariantParam } from "@/lib/util/product-variant-url"
import { resolveImageUrl } from "@/lib/util/image-url"
import { CheckCircle2, ShoppingBag } from "lucide-react"
import { useTranslation } from "react-i18next"

type GoToCartModalProps = {
  isOpen: boolean
  closeModal: () => void
  product: StoreProduct
  similarProducts: StoreProduct[]
  variant: StoreProductVariant
  quantity: number
  selectedVariant: StoreProductVariant
  addedQuantity?: number
}

const GoToCartModal = ({
  similarProducts,
  isOpen,
  closeModal,
  product,
  variant,
  quantity,
  selectedVariant,
  addedQuantity,
}: GoToCartModalProps) => {
  const { t } = useTranslation()
  let thumbnailUrl: string | null = null

  if (selectedVariant?.images && selectedVariant.images.length > 0) {
    const thumbnail =
      selectedVariant.images?.find((image) => image.is_thumbnail === 1) ||
      selectedVariant.images?.[0]

    if (thumbnail) {
      thumbnailUrl = resolveImageUrl(thumbnail.image_key)
    }
  } else {
    const thumbnail = product.images?.find((image) => image.is_thumbnail === 1)
    if (thumbnail) {
      thumbnailUrl = resolveImageUrl(thumbnail.image_key)
    }
  }

  const displayQty =
    typeof addedQuantity === "number" && addedQuantity > 0 ? addedQuantity : quantity

  const lineTotal = (() => {
    let unitPrice = selectedVariant?.sales_price ?? variant?.sales_price ?? 0
    if (
      selectedVariant?.stocks &&
      selectedVariant.stocks.length > displayQty - 1 &&
      selectedVariant.stocks[displayQty - 1] &&
      typeof selectedVariant.stocks[displayQty - 1].sales_price === "number"
    ) {
      unitPrice = selectedVariant.stocks[displayQty - 1].sales_price
    }
    return (unitPrice * displayQty).toLocaleString("tr-TR", {
      style: "currency",
      currency: "TRY",
    })
  })()

  return (
    <Modal isOpen={isOpen} close={closeModal} size="large">
      <div className="flex max-h-[70vh] flex-col overflow-y-auto">
        <div className="mb-6 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100">
            <CheckCircle2 className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <Heading className="text-xl font-semibold text-foreground">
              {t("product.addedToCart")}
            </Heading>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {t("product.quantity")}: {displayQty}
            </p>
          </div>
        </div>

        <div className="mb-6 flex gap-4 rounded-xl border border-muted-accent/80 bg-muted/20 p-4">
          {thumbnailUrl && (
            <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-lg border border-muted-accent/60 bg-white">
              <Image
                height={96}
                width={96}
                src={thumbnailUrl}
                alt={normalizeTextEncoding(product.title ?? "")}
                className="h-full w-full object-contain p-1"
              />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold leading-snug text-foreground">
              {normalizeTextEncoding(product.title ?? "")}
            </h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {normalizeTextEncoding(variant.title ?? "")}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("product.seller")}: 101 YAPI
            </p>
            <div className="mt-3 flex items-end justify-between gap-2">
              <span className="text-sm text-muted-foreground">
                {displayQty} {t("product.quantity").toLowerCase()}
              </span>
              <span className="text-lg font-bold text-foreground">{lineTotal}</span>
            </div>
          </div>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Link href="/cart" className="block">
            <button
              type="button"
              className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-orange-200 bg-white px-6 py-3 text-base font-semibold text-slate-700 shadow-sm transition-colors hover:bg-orange-50 hover:text-orange-700"
            >
              <ShoppingBag className="h-5 w-5" />
              {t("product.goToCart")}
            </button>
          </Link>
          <button
            type="button"
            onClick={closeModal}
            className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-muted-accent bg-white px-6 py-3 text-base font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50"
          >
            {t("product.continueShopping")}
          </button>
        </div>

        {similarProducts.length > 0 && (
          <div className="border-t border-muted-accent/60 pt-6">
            <h4 className="mb-4 text-base font-semibold text-foreground">
              {t("product.similarProducts")}
            </h4>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {similarProducts.flatMap((similarProduct) =>
                (similarProduct.variants || []).map((similarVariant) => {
                  const href = `/products/${similarProduct.handle}${
                    similarVariant.title
                      ? `?variant=${getProductVariantParam(similarVariant.title)}`
                      : ""
                  }`
                  const imageKey = similarVariant.images?.[0]?.image_key
                  const price = (similarVariant.sales_price ?? 0).toLocaleString("tr-TR", {
                    style: "currency",
                    currency: "TRY",
                  })

                  return (
                    <Link
                      key={similarVariant.id}
                      href={href}
                      className="group min-w-[140px] max-w-[160px] shrink-0"
                    >
                      <div className="overflow-hidden rounded-xl border border-muted-accent/60 bg-white shadow-sm transition-shadow group-hover:shadow-md">
                        <div className="relative aspect-square bg-muted/30">
                          {imageKey ? (
                            <Image
                              src={resolveImageUrl(imageKey) || ""}
                              alt={
                                normalizeTextEncoding(similarVariant.title ?? "") ||
                                normalizeTextEncoding(similarProduct.title ?? "")
                              }
                              fill
                              className="object-cover transition-transform group-hover:scale-105"
                              sizes="160px"
                            />
                          ) : null}
                        </div>
                        <div className="p-2.5">
                          <p className="line-clamp-2 text-xs font-medium leading-tight text-foreground">
                            {normalizeTextEncoding(similarVariant.title ?? "")}
                          </p>
                          <p className="mt-1 text-sm font-bold text-orange-600">{price}</p>
                        </div>
                      </div>
                    </Link>
                  )
                })
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default GoToCartModal
